import React, { useEffect, useMemo, useRef, useState } from 'react';
import { makeStyles, TextareaAutosize } from '@material-ui/core';
import DrugSelect from './DrugSelect';
import FormSelect from './FormSelect';
import { PropTypes } from 'prop-types';
import { Clear } from '@material-ui/icons';
const styles = makeStyles((theme) => ({
    drugContainer: {
        display: 'grid',
        alignItems: 'center',
        position: 'relative',
        marginBottom: '21px',
        fontFamily: 'mainFont',
        fontSize: '14px',
        fontWeight: '600',
        fontStretch: 'normal',
        fontStyle: 'normal',
        lineHeight: '1.5',
        letterSpacing: 'normal',
        textAlign: 'left',
        color: '#232733',
        '&:focus-within *, &:visited *, &:focus-visible * ': {
            borderColor: '#1b90fa'
        },
        gridTemplateColumns: '3fr 1fr 2fr 3fr',
        gridGap: '10px',

        [theme.breakpoints.down('1025')]: {
            gridGap: '0px',
            '& > div': {
                minWidth: '200px',
                marginRight: '0'
            },
            '& > div:nth-of-type(2)': {
                minWidth: '120px'
            }
        }
    },
    textarea: {
        border: 'none',
        borderBottom: '1px solid #bbbbbb',
        color: '#363636',
        fontFamily: 'inherit',
        fontSize: 'inherit',
        padding: '0',
        '&::-webkit-resizer': {
            display: 'none'
        },
        '&:focus': {
            outline: 'none'
        }
    },
    cancel: {
        color: '#bbbbbb',
        // marginTop: 'px',
        height: '16px',
        position: 'absolute',
        left: '-20px',
        // bottom: '3px',
        '&:hover': {
            cursor: 'pointer'
        }
    }
}));

const DrugEdit = ({ drugItem, removeDrug, editDrug, drugs, handleSearchDrugs }) => {
    const classes = styles();
    const [searchTerm, setSearchTerm] = useState('');
    const [strengths, setStrengths] = useState(() => {
        if (drugItem && drugItem.drug && drugItem.drug_form) {
            const form = drugItem.drug?.forms?.find((val) => val.id === drugItem.drug_form);
            if (form) {
                return form.strengths;
            }
        }
        return [];
    });
    const [forms, setForms] = useState(() => {
        if (drugItem && drugItem.drug && drugItem.drug?.forms) {
            return drugItem.drug?.forms;
        }
        return [];
    });
    const [isLoading, setIsLoading] = useState(false);
    const [disableAutoOpen, setDisableAutoOpen] = useState(() => {
        if (drugItem?.drug && !drugItem.drug.forms) {
            return true;
        }
        return false;
    });
    const descriptionRef = useRef();
    // handle side effect of drug changes because of editing drugs as drug in view mode is not related to any forms and strengths list to select
    useEffect(() => {
        const found = drugs?.find((val) => val.id === drugItem?.drug?.id);
        if (drugs && found) {
            setForms(found?.forms);
            const form = found.forms.find((val) => val.id === drugItem.drug_form);
            if (form) {
                setStrengths(form?.strengths);
            }
        }
        setIsLoading(false);
    }, [drugs]);
    const handleDrugChange = (value) => {
        setSearchTerm(value);
        const found = drugs?.find((val) => val.name.toLowerCase() === value.toLowerCase());
        if (drugs && found) {
            // setIsPopupOpen(false);
            return;
        }
        if (!value || value === drugItem.drug?.name) {
            handleSearchDrugs(null);
            // setIsPopupOpen(false);
            return;
        }
        if (value.length < 2) {
            return;
        }
        setIsLoading(true);
        // setIsPopupOpen(true);
        let timeout;
        if (timeout) {
            clearTimeout();
        }
        timeout = setTimeout(() => {
            if (value) {
                const term = value?.trim();
                handleSearchDrugs(term);
            }
        }, 1200);
    };
    const handleDrugSelected = (value) => {
        // setIsPopupOpen(false);
        setDisableAutoOpen(false);
        if (value?.id === drugItem?.drug?.id) {
            return;
        }
        if (value) {
            const result = editDrug({ value, prop: 'drug' });
            if (result === false) {
                if (drugItem?.drug) {
                    setSearchTerm(drugItem?.drug?.name);
                }
            }
            // setForms(value?.forms);
        }
    };
    const handleFormSelect = (event) => {
        const value = event?.target?.value;
        if (value) {
            let selectedForm = drugItem.drug?.forms?.find((val) => val.id === value);
            if (!selectedForm) {
                selectedForm = forms?.find((val) => val.id === value);
            }
            setStrengths(selectedForm?.strengths);
            editDrug({ value, prop: 'drug_form' });
        }
    };
    const handleStrengthSelect = (event) => {
        const value = event?.target?.value;
        editDrug({ value, prop: 'drug_strength' });
        setTimeout(() => {
            descriptionRef.current.focus();
        }, 100);
    };
    const handleDescriptionChange = (event) => {
        const value = event?.target?.value;
        editDrug({ value, prop: 'description' });
    };
    const drugSelect = useMemo(
        () => (
            <DrugSelect
                value={drugItem?.drug}
                inputValue={searchTerm}
                options={drugs}
                handleDrugChange={handleDrugChange}
                handleDrugSelected={handleDrugSelected}
                isLoading={isLoading}
            />
        ),
        [drugItem, searchTerm, drugs, isLoading]
    );
    const formSelect = useMemo(
        () => (
            <FormSelect
                disabled={!drugItem?.drug}
                value={drugItem?.drug_form}
                prevField={drugItem?.drug?.id}
                options={forms}
                handleSelect={handleFormSelect}
                disableAutoOpen={disableAutoOpen}
                dataTestId="drugForm"
            />
        ),
        [drugItem, forms]
    );
    const strengthSelect = useMemo(
        () => (
            <FormSelect
                disabled={!drugItem?.drug_form}
                value={drugItem?.drug_strength}
                options={strengths}
                prevField={drugItem.drug_form}
                handleSelect={handleStrengthSelect}
                disableAutoOpen={disableAutoOpen}
                dataTestId="drugStrength"
            />
        ),
        [drugItem, strengths]
    );
    const description = useMemo(
        () => (
            <TextareaAutosize
                value={drugItem?.description}
                className={classes.textarea}
                onChange={handleDescriptionChange}
                ref={descriptionRef}
                data-testid="drugDesc"
            />
        ),
        [drugItem]
    );
    return (
        <div className={classes.drugContainer}>
            {drugItem?.drug && (
                <Clear className={classes.cancel} onClick={removeDrug} data-testid="delDrug" />
            )}
            <div className={classes.drugSelect}>{drugSelect}</div>
            <div className={classes.formSelect}>{formSelect}</div>
            <div className={classes.strengthSelect}>{strengthSelect}</div>
            <div className={classes.description}>{description}</div>
        </div>
    );
};
DrugEdit.propTypes = {
    drugItem: PropTypes.object,
    removeDrug: PropTypes.func,
    editDrug: PropTypes.func,
    handleSearchDrugs: PropTypes.func,
    drugs: PropTypes.array,
    drugEdited: PropTypes.bool
};
export default DrugEdit;
